// Data adapter for embedded transactions. Routes the embed's model + CRUD
// calls to the right backend depending on the runtime's DataAccess:
//
//   • site mode   → /website/{userId}/{project}/site-txn/*  (site-auth; works
//                   for real end-users on a published/internal page)
//   • otherwise   → the author-only /designers + /livepreview endpoints
//                   (designer canvas / in-app author preview)
//
// Same call shapes as the platform functions so useEmbedRecords / the capture
// previews swap transparently by passing `dataAccess`.

import type { DataAccess } from '@/pages/workbench/smartpage-preview/dataBinding'
import { loadTransactionObject } from '@/api/designers'
import {
  siteTxnModel, siteTxnList, siteTxnResolveDefaults, siteTxnExecute,
  siteTxnExecuteBatch, siteTxnProcess, siteTxnRetrieve, siteTxnRights, siteDocUpload,
} from '@/api/website'
import { signInRequiredOf, type SignInRequired } from '@twasta/page-runtime/signInPrompt'
import { uploadDocument as designerUploadDocument } from '@/api/documentStore'
import {
  livePreviewList, livePreviewResolveDefaults, livePreviewExecute,
  livePreviewExecuteBatch, livePreviewProcess, livePreviewRetrieve,
  type BatchSaveBody, type BatchSaveResult, type ListFilter,
  type FilterSetSelection,
} from './api'
import type { EmbedBatchOutcome, EmbedBatchRecordInput, EmbedRetrieveResponse, ListResponse, RunActionResponse } from './types'

// `content` is the raw transaction JSON; left loose (like the platform
// loadTransactionObject) so the capture previews unwrap `content.transaction`
// without per-call casts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TxnModel = { success: boolean; content: any; message?: string }

// What the caller may do on an embedded transaction — drives button visibility.
export interface EmbedRights {
  mode: string
  canList: boolean; canLoad: boolean
  canAdd: boolean; canEdit: boolean; canDelete: boolean
  actions: string[]          // granted custom-action names; ['*'] = all
}

interface SiteCtx {
  userId: string
  project: string
  embedToken?: string
  onEmbedTokenExpired?: () => Promise<string | null>
  onSignInRequired?: (refusal: SignInRequired) => void
  embedFilterValues?: Record<string, unknown>
}

function siteOf(da?: DataAccess): SiteCtx | null {
  return da && da.mode === 'site'
    ? {
        userId: da.userId, project: da.project,
        embedToken: da.embedToken, onEmbedTokenExpired: da.onEmbedTokenExpired,
        onSignInRequired: da.onSignInRequired,
        embedFilterValues: da.embedFilterValues,
      }
    : null
}

// Run a site call with the current embed token; on a 401 EMBED_TOKEN_EXPIRED,
// re-fetch the page model once for a fresh token and retry. `fn` receives the
// token to use so the retry can pass the refreshed one. A refusal that means
// "this needs a signed-in account" (SIGN_IN_REQUIRED / SESSION_EXPIRED /
// PROJECT_USER_REQUIRED) is reported to the host through `onSignInRequired`,
// which swaps the embed for the shared sign-in card — and still rethrown, so
// every caller's own error handling is unchanged.
async function withEmbedRetry<T>(
  site: SiteCtx, fn: (token?: string) => Promise<T>,
): Promise<T> {
  try {
    return await fn(site.embedToken)
  } catch (err) {
    const resp = (err as { response?: { status?: number; data?: { error?: { code?: string } } } })?.response
    if (resp?.status === 401 && resp?.data?.error?.code === 'EMBED_TOKEN_EXPIRED' && site.onEmbedTokenExpired) {
      const fresh = await site.onEmbedTokenExpired()
      if (fresh) {
        try {
          return await fn(fresh)
        } catch (retryErr) {
          reportSignIn(site, retryErr)
          throw retryErr
        }
      }
    }
    reportSignIn(site, err)
    throw err
  }
}

/** `withEmbedRetry` for another site transport over the same DataAccess (the
 *  wizard embed's, `runtime/siteWizardHost.tsx`): the embed token refresh on
 *  EMBED_TOKEN_EXPIRED and the sign-in card hand-off, in one place. */
export async function withSiteEmbedRetry<T>(
  da: DataAccess & { mode: 'site' }, fn: (token?: string) => Promise<T>,
): Promise<T> {
  const site = siteOf(da)
  if (!site) return fn(undefined)
  return withEmbedRetry(site, fn)
}

/** True when `err` is a "sign in to see this" refusal the HOST already
 *  answers with the shared sign-in card (published site with an
 *  `onSignInRequired` hook). Callers skip their own error toast then — the card
 *  IS the message, and a toast beside it reads as a failure. */
export function signInHandledByHost(da: DataAccess | undefined, err: unknown): boolean {
  const site = siteOf(da)
  return !!(site && site.onSignInRequired && signInRequiredOf(err))
}

function reportSignIn(site: SiteCtx, err: unknown): void {
  const refusal = signInRequiredOf(err)
  if (!refusal || !site.onSignInRequired) return
  try {
    site.onSignInRequired(refusal)
  } catch (hookErr) {
    // A broken host hook must not mask the refusal the caller is about to see.
    console.warn('[embed] sign-in hook failed', hookErr)
  }
}

// Document/image upload transport for an embed's FormRenderer: routes to the
// public site-doc endpoint in site mode, else the author document store.
// Returns the served URL to store on the column.
export function makeEmbedUploader(
  da: DataAccess | undefined, project: string,
): (file: File, meta?: { docType?: string; recordId?: string; replaces?: string }) => Promise<{ url: string; name?: string }> {
  const site = siteOf(da)
  return async (file: File, meta?: { docType?: string; recordId?: string; replaces?: string }) => {
    if (site) {
      const doc = (await withEmbedRetry(site, (tok) =>
        siteDocUpload(site.userId, project, file, meta, tok))).data.document
      return { url: doc.url, name: doc.name }
    }
    const doc = await designerUploadDocument(project, file, meta)
    return { url: doc.url, name: doc.name }
  }
}

export async function embedLoadModel(da: DataAccess | undefined, project: string, objName: string): Promise<TxnModel> {
  const site = siteOf(da)
  if (site) return (await withEmbedRetry(site, (tok) => siteTxnModel(site.userId, project, objName, tok))).data as TxnModel
  return loadTransactionObject(project, objName) as Promise<TxnModel>
}

// Fetch the caller's rights for an embedded transaction. Non-site (author /
// in-app preview) surfaces are unrestricted, preserving current behaviour.
export async function embedRights(
  da: DataAccess | undefined, project: string, objName: string,
): Promise<EmbedRights> {
  const site = siteOf(da)
  if (!site) {
    return { mode: 'full', canList: true, canLoad: true, canAdd: true, canEdit: true, canDelete: true, actions: ['*'] }
  }
  const r = (await withEmbedRetry(site, (tok) => siteTxnRights(site.userId, project, objName, tok))).data
  return {
    mode: r.mode, canList: r.canList, canLoad: r.canLoad,
    canAdd: r.canAdd, canEdit: r.canEdit, canDelete: r.canDelete, actions: r.actions || [],
  }
}

export async function embedList(
  da: DataAccess | undefined, project: string, objName: string,
  limit = 50, offset = 0, formNo?: string,
  parentPk?: Record<string, unknown>, filters?: ListFilter[],
  enforcedOverrides?: Record<string, { from?: string; to?: string; value?: string }>,
  filterSet?: FilterSetSelection,
  embedFilterValues?: Record<string, unknown>,
): Promise<ListResponse> {
  const site = siteOf(da)
  if (site) {
    return (await withEmbedRetry(site, (tok) => siteTxnList(site.userId, project, objName, {
      limit, offset, form_no: formNo, parent_pk: parentPk, filters,
      enforced_overrides: enforcedOverrides,
      enforced_filter_set_id: filterSet?.bypass ? undefined : filterSet?.filterSetId,
      bypass_enforced: filterSet?.bypass || undefined,
      embed_filter_values: embedFilterValues ?? site.embedFilterValues,
    }, tok))).data as ListResponse
  }
  return livePreviewList(
    project, objName, limit, offset, formNo, parentPk, filters, enforcedOverrides,
    filterSet,
  )
}

export async function embedResolveDefaults(
  da: DataAccess | undefined, project: string, objName: string,
): Promise<{ ok?: boolean; values: Record<string, unknown> }> {
  const site = siteOf(da)
  if (site) return (await withEmbedRetry(site, (tok) => siteTxnResolveDefaults(site.userId, project, objName, tok))).data
  return livePreviewResolveDefaults(project, objName)
}

export async function embedExecute(
  da: DataAccess | undefined, project: string, objName: string,
  action: string, payload: Record<string, unknown> = {},
  embedFilterValues?: Record<string, unknown>,
): Promise<unknown> {
  const site = siteOf(da)
  if (site) return (await withEmbedRetry(site, (tok) =>
    siteTxnExecute(site.userId, project, objName, action, payload, tok,
      embedFilterValues ?? site.embedFilterValues))).data
  return livePreviewExecute(project, objName, action, payload)
}

// Run a custom action button, routed to the site-txn endpoint for end-users
// (deployed public site) else the author-only live-preview endpoint. Throws on
// a pre-action validation failure (the error envelope carries the messages).
export async function embedRunAction(
  da: DataAccess | undefined, project: string, objName: string,
  req: {
    formNo: string
    actionName: string
    header: Record<string, unknown>
    headerValues?: Record<string, unknown>
    txnId?: string
    parentPk?: Record<string, unknown>
    ignoreWarnings?: string[]
    confirm?: { acknowledged?: boolean; answer?: string; resend?: boolean }
  },
): Promise<RunActionResponse> {
  return await embedExecute(da, project, objName, 'run_action', {
    form_no: req.formNo,
    action_name: req.actionName,
    header: req.header,
    header_values: req.headerValues || {},
    ...(req.txnId ? { txn_id: req.txnId } : {}),
    ...(req.parentPk && Object.keys(req.parentPk).length > 0 ? { parent_pk: req.parentPk } : {}),
    ...(req.ignoreWarnings?.length ? { ignore_warnings: req.ignoreWarnings } : {}),
    ...(req.confirm ? { confirm: req.confirm } : {}),
  }) as RunActionResponse
}

// Apply an action's item_change-style result to a record — moved verbatim to
// the shared package (txn/capture contract) so the standalone runtime applies
// action results identically; re-exported so existing importers keep working.
export { applyActionResultViaOnChange } from '@twasta/page-runtime/txn/capture'

export async function embedProcess(
  da: DataAccess | undefined, project: string, objName: string,
  records: EmbedBatchRecordInput[], opts: { documentId?: string } = {},
): Promise<EmbedBatchOutcome> {
  const site = siteOf(da)
  if (site) {
    return (await withEmbedRetry(site, (tok) =>
      siteTxnProcess(site.userId, project, objName, records, opts.documentId, tok))).data as EmbedBatchOutcome
  }
  return livePreviewProcess(project, objName, records, opts)
}

// Atomic multi-form save: header + its nested child/grandchild rows committed
// in ONE DB transaction (or rolled back as a unit). Routes to the site-txn
// path for end-users, else the author-only live-preview endpoint.
export async function embedExecuteBatch(
  da: DataAccess | undefined, project: string, objName: string,
  body: BatchSaveBody,
): Promise<BatchSaveResult> {
  const site = siteOf(da)
  if (site) {
    return (await withEmbedRetry(site, (tok) =>
      siteTxnExecuteBatch(site.userId, project, objName, body, tok, site.embedFilterValues))).data as BatchSaveResult
  }
  return livePreviewExecuteBatch(project, objName, body)
}

export async function embedRetrieve(
  da: DataAccess | undefined, project: string, objName: string, txnId: string,
): Promise<EmbedRetrieveResponse | null> {
  const site = siteOf(da)
  if (site) {
    try {
      return (await withEmbedRetry(site, (tok) =>
        siteTxnRetrieve(site.userId, project, objName, txnId, tok))).data as EmbedRetrieveResponse
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) return null
      throw err
    }
  }
  return livePreviewRetrieve(project, objName, txnId)
}
